import type { LegendChip, Legend as LegendSpec } from "../map/types";

interface LegendProps {
  legend: LegendSpec;
  /**
   * Makes the chips interactive. Passed only where picking a chip means
   * something on the map — without it they stay plain swatches.
   */
  selected?: string[];
  onSelect?: (label: string) => void;
  /** Pick a whole heading's worth of chips at once. */
  onSelectMany?: (labels: string[]) => void;
  onClear?: () => void;
}

/** Chips in registry order, split into the headings they are filed under. */
function byGroup(chips: LegendChip[]): { name: string; chips: LegendChip[] }[] {
  const groups: { name: string; chips: LegendChip[] }[] = [];
  for (const chip of chips) {
    const name = chip.group ?? "";
    const last = groups.at(-1);
    if (last && last.name === name) last.chips.push(chip);
    else groups.push({ name, chips: [chip] });
  }
  return groups;
}

export function Legend({
  legend,
  selected,
  onSelect,
  onSelectMany,
  onClear,
}: LegendProps) {
  const focused = selected ?? [];
  const interactive = Boolean(onSelect);

  return (
    <div className="ml-6.5 mt-2 rounded-lg bg-surface-alt px-2.5 py-2">
      {legend.unit && (
        <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-ink-soft">
          {legend.unit}
        </div>
      )}
      {legend.chips && (
        <div className="space-y-1.5">
          {byGroup(legend.chips).map((group) => {
            const labels = group.chips.map((c) => c.label);
            const allOn = labels.every((l) => focused.includes(l));
            return (
              <div key={group.name}>
                {group.name &&
                  (onSelectMany ? (
                    <button
                      type="button"
                      onClick={() => onSelectMany(labels)}
                      title={
                        allOn
                          ? `Stop highlighting every ${group.name} line`
                          : `Highlight every ${group.name} line`
                      }
                      className={`mb-1 block cursor-pointer text-[9px] font-semibold uppercase tracking-wider hover:text-ink hover:underline ${
                        allOn ? "text-accent" : "text-ink-soft"
                      }`}
                    >
                      {group.name}
                    </button>
                  ) : (
                    <div className="mb-1 text-[9px] font-semibold uppercase tracking-wider text-ink-soft">
                      {group.name}
                    </div>
                  ))}
                <ul className="flex flex-wrap gap-1">
                  {group.chips.map((chip) => {
                    // With nothing picked every chip reads normally; once
                    // anything is picked, the rest fade to match the map.
                    const on = focused.includes(chip.label);
                    const faded = interactive && focused.length > 0 && !on;
                    const swatch = (
                      <span
                        className={`flex h-4.5 min-w-4.5 items-center justify-center rounded px-1 text-[9px] font-semibold leading-none transition-opacity ${
                          faded ? "opacity-30" : ""
                        }`}
                        style={{ background: chip.color, color: chip.textColor }}
                      >
                        {chip.label}
                      </span>
                    );
                    if (!interactive) return <li key={chip.label}>{swatch}</li>;
                    return (
                      <li key={chip.label}>
                        <button
                          type="button"
                          aria-pressed={on}
                          title={
                            on
                              ? `Stop highlighting ${chip.label}`
                              : `Highlight ${chip.label}`
                          }
                          onClick={() => onSelect?.(chip.label)}
                          className={`block cursor-pointer rounded ${
                            on ? "ring-2 ring-ink ring-offset-1 ring-offset-surface-alt" : ""
                          }`}
                        >
                          {swatch}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      <ul className="space-y-1">
        {legend.stops.map((stop) => (
          <li key={stop.label} className="flex items-center gap-2">
            <span
              className="h-3 w-3 shrink-0 rounded-sm"
              style={{ background: stop.color }}
            />
            <span className="text-[11px] tabular-nums text-ink-soft">{stop.label}</span>
          </li>
        ))}
      </ul>
      {/* The reset replaces the note while a focus is on: it is the only thing
          worth reading at that point, and it keeps the panel one line tall. */}
      {(legend.note || focused.length > 0) && (
        <p className="mt-1.5 text-[10px] leading-snug text-ink-soft">
          {focused.length > 0 ? (
            <button
              type="button"
              onClick={onClear}
              className="cursor-pointer font-medium text-accent hover:underline"
            >
              Show all lines
              {focused.length > 1 ? ` (${focused.length} highlighted)` : ""}
            </button>
          ) : (
            legend.note
          )}
        </p>
      )}
    </div>
  );
}
